import React, { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";

import { QueryErrorBanner, KpiStripSkeleton } from "../../components/feedback/QueryFeedback";
import { artistsApi, budgetsApi, eventsApi, invoicesApi, tasksApi, venuesApi } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { MobileSelect } from "../../components/ui/MobileSelect";
import { queryKeys } from "../../queryKeys";
import { workspaceNav } from "../../routes/workspaceNavigation";
import type { Artist, Budget, DashboardData, EventListItem, InvoiceRecord, Task, Venue } from "../../types";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import { formatINR, formatINRCompact } from "../../utils/format";
import { formatMarginPct, formatPct, formatSellThroughPct } from "../../utils/format";
import { CHART_BAR_CURSOR, chartTooltipProps } from "../../constants/chartTheme";
import "./EventsAnalyticsPage.css";

const CHART_AXIS_TICK = { fill: "#b2b3c1", fontSize: 12 };

const COLORS = {
  revenue: "#22C55E",
  spend: "#E50914",
  profit: "#06B6D4",
  projected: "#A855F7",
  amber: "#F59E0B",
};
const PIE_COLORS = ["#06B6D4", "#22C55E", "#A855F7", "#F59E0B", "#E50914", "#38BDF8", "#FB7185", "#A3E635"];
const STATUS_COLORS: Record<string, string> = {
  completed: "#22C55E",
  confirmed: "#06B6D4",
  live: "#38BDF8",
  draft: "#94A3B8",
  cancelled: "#E50914",
  planning: "#F59E0B",
};
const RISK_COLORS: Record<string, string> = {
  low: "#22C55E",
  medium: "#F59E0B",
  high: "#FB7185",
  critical: "#E50914",
  unknown: "#94A3B8",
};

type RangeMode = "all" | "lastN" | "rolling" | "range";
type RollingUnit = "days" | "weeks" | "months";
type SortKey = "date" | "revenue" | "profit" | "margin" | "sellThrough";

type AnalyticsFilters = {
  mode: RangeMode;
  lastN: number;
  rollingUnit: RollingUnit;
  rollingValue: number;
  /** Whole periods (rolling) or blocks of N events (lastN) stepped back from the newest window. */
  offset: number;
  from: string;
  to: string;
  artist: string;
  genre: string;
  venue: string;
  city: string;
  status: string;
  search: string;
  sort: SortKey;
  sortDir: "asc" | "desc";
  deepOps: boolean;
};

const DEFAULT_FILTERS: AnalyticsFilters = {
  mode: "all",
  lastN: 5,
  rollingUnit: "months",
  rollingValue: 3,
  offset: 0,
  from: "",
  to: "",
  artist: "all",
  genre: "all",
  venue: "all",
  city: "all",
  status: "all",
  search: "",
  sort: "date",
  sortDir: "desc",
  deepOps: false,
};

const ROLLING_UNIT_DAYS: Record<RollingUnit, number> = { days: 1, weeks: 7, months: 30 };

function parseFilters(params: URLSearchParams): AnalyticsFilters {
  const mode = (params.get("mode") as RangeMode) || DEFAULT_FILTERS.mode;
  const rollingUnit = (params.get("unit") as RollingUnit) || DEFAULT_FILTERS.rollingUnit;
  const sort = (params.get("sort") as SortKey) || DEFAULT_FILTERS.sort;
  const lastN = Number(params.get("n"));
  const rollingValue = Number(params.get("v"));
  const offset = Number(params.get("off"));
  return {
    mode: ["all", "lastN", "rolling", "range"].includes(mode) ? mode : DEFAULT_FILTERS.mode,
    lastN: Number.isFinite(lastN) && lastN > 0 ? Math.floor(lastN) : DEFAULT_FILTERS.lastN,
    rollingUnit: ["days", "weeks", "months"].includes(rollingUnit) ? rollingUnit : DEFAULT_FILTERS.rollingUnit,
    rollingValue: Number.isFinite(rollingValue) && rollingValue > 0 ? Math.floor(rollingValue) : DEFAULT_FILTERS.rollingValue,
    offset: Number.isFinite(offset) && offset > 0 ? Math.floor(offset) : DEFAULT_FILTERS.offset,
    from: params.get("from") || "",
    to: params.get("to") || "",
    artist: params.get("artist") || "all",
    genre: params.get("genre") || "all",
    venue: params.get("venue") || "all",
    city: params.get("city") || "all",
    status: params.get("status") || "all",
    search: params.get("q") || "",
    sort: ["date", "revenue", "profit", "margin", "sellThrough"].includes(sort) ? sort : DEFAULT_FILTERS.sort,
    sortDir: params.get("dir") === "asc" ? "asc" : "desc",
    deepOps: params.get("deep") === "1",
  };
}

function eventTime(event: EventListItem): number {
  const t = new Date(event.event_date).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function artistKey(event: EventListItem): string {
  return event.artist_id || event.artist || "";
}

const DAY_MS = 86_400_000;

type EventSelection = {
  events: EventListItem[];
  /** True when events exist before the current window, so "previous" has somewhere to go. */
  hasOlder: boolean;
  rollingWindow: { start: number; end: number } | null;
};

function selectEvents(
  events: EventListItem[],
  filters: AnalyticsFilters,
  genreByArtistId: Map<string, string>,
): EventSelection {
  let result = [...events];
  let hasOlder = false;
  let rollingWindow: EventSelection["rollingWindow"] = null;

  if (filters.search.trim()) {
    const q = filters.search.trim().toLowerCase();
    result = result.filter(
      (e) => e.name.toLowerCase().includes(q) || (e.artist || "").toLowerCase().includes(q) || (e.city || "").toLowerCase().includes(q),
    );
  }
  if (filters.artist !== "all") result = result.filter((e) => artistKey(e) === filters.artist);
  if (filters.genre !== "all") result = result.filter((e) => (genreByArtistId.get(e.artist_id || "") || "Unlabelled") === filters.genre);
  if (filters.venue !== "all") result = result.filter((e) => (e.venue_id || "") === filters.venue);
  if (filters.city !== "all") result = result.filter((e) => (e.city || "").toLowerCase() === filters.city.toLowerCase());
  if (filters.status !== "all") result = result.filter((e) => e.status === filters.status);

  result.sort((a, b) => eventTime(b) - eventTime(a));

  if (filters.mode === "rolling") {
    const span = filters.rollingValue * ROLLING_UNIT_DAYS[filters.rollingUnit] * DAY_MS;
    const end = Date.now() - filters.offset * span;
    const start = end - span;
    rollingWindow = { start, end };
    hasOlder = result.some((e) => {
      const t = eventTime(e);
      return t > 0 && t < start;
    });
    // The current window stays open-ended (upcoming events still count); only a
    // shifted window gets an upper bound so adjacent windows never overlap.
    result = result.filter((e) => {
      const t = eventTime(e);
      return t >= start && (filters.offset === 0 || t < end);
    });
  } else if (filters.mode === "lastN") {
    const begin = filters.offset * filters.lastN;
    hasOlder = result.length > begin + filters.lastN;
    result = result.slice(begin, begin + filters.lastN);
  } else if (filters.mode === "range") {
    const fromTs = filters.from ? new Date(filters.from).getTime() : -Infinity;
    const toTs = filters.to ? new Date(filters.to).getTime() + DAY_MS : Infinity;
    hasOlder = result.some((e) => {
      const t = eventTime(e);
      return t > 0 && t < fromTs;
    });
    result = result.filter((e) => {
      const t = eventTime(e);
      return t >= fromTs && t < toTs;
    });
  }

  return { events: result, hasOlder, rollingWindow };
}

function localDateInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function shiftDateInput(value: string, days: number): string {
  const d = new Date(value);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function formatWindowDate(ts: number): string {
  return new Date(ts).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function actualRevenueOf(d: DashboardData): number {
  return Number(d.review?.total_revenue ?? d.metrics.actual_revenue ?? d.ticket_summary.revenue_generated ?? 0);
}
function totalCostOf(d: DashboardData): number {
  return Number(d.metrics.total_cost ?? 0);
}
function actualProfitOf(d: DashboardData): number {
  return Number(d.review?.net_profit ?? d.metrics.actual_profit ?? actualRevenueOf(d) - totalCostOf(d));
}

type Props = {
  workspace: WorkspaceId;
};

const EventsAnalyticsPage: React.FC<Props> = ({ workspace }) => {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

  const eventsQuery = useQuery({
    queryKey: token ? queryKeys.events.list(workspace) : queryKeys.events.all(workspace),
    queryFn: () => eventsApi.list(token!),
    enabled: Boolean(token),
  });
  const artistsQuery = useQuery({
    queryKey: token ? queryKeys.artists.list(workspace) : queryKeys.artists.all(workspace),
    queryFn: () => artistsApi.list(token!),
    enabled: Boolean(token),
  });
  const venuesQuery = useQuery({
    queryKey: token ? queryKeys.venues.list(workspace) : queryKeys.venues.all(workspace),
    queryFn: () => venuesApi.list(token!),
    enabled: Boolean(token),
  });

  const allEvents = useMemo<EventListItem[]>(() => {
    const data = eventsQuery.data;
    return Array.isArray(data) ? data : [];
  }, [eventsQuery.data]);
  const artists = useMemo<Artist[]>(() => artistsQuery.data ?? [], [artistsQuery.data]);
  const venues = useMemo<Venue[]>(() => venuesQuery.data ?? [], [venuesQuery.data]);

  const genreByArtistId = useMemo(() => {
    const map = new Map<string, string>();
    artists.forEach((a) => map.set(a.id, a.genre?.trim() || "Unlabelled"));
    return map;
  }, [artists]);
  const venueNameById = useMemo(() => {
    const map = new Map<string, string>();
    venues.forEach((v) => map.set(v.id, v.venue_name));
    return map;
  }, [venues]);

  const artistOptions = useMemo(() => {
    const map = new Map<string, string>();
    allEvents.forEach((e) => {
      const key = artistKey(e);
      if (key) map.set(key, e.artist || key);
    });
    return Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [allEvents]);
  const genreOptions = useMemo(() => {
    const set = new Set<string>();
    allEvents.forEach((e) => set.add(genreByArtistId.get(e.artist_id || "") || "Unlabelled"));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allEvents, genreByArtistId]);
  const venueOptions = useMemo(() => {
    const map = new Map<string, string>();
    allEvents.forEach((e) => {
      if (e.venue_id) map.set(e.venue_id, venueNameById.get(e.venue_id) || "Venue");
    });
    return Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [allEvents, venueNameById]);
  const cityOptions = useMemo(() => {
    const set = new Set<string>();
    allEvents.forEach((e) => e.city && set.add(e.city));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allEvents]);
  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    allEvents.forEach((e) => e.status && set.add(e.status));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allEvents]);

  const selection = useMemo(
    () => selectEvents(allEvents, filters, genreByArtistId),
    [allEvents, filters, genreByArtistId],
  );
  const filteredEvents = selection.events;

  const dashboardResults = useQueries({
    queries: filteredEvents.map((event) => ({
      queryKey: queryKeys.events.dashboard(workspace, event.id),
      queryFn: () => eventsApi.dashboard(event.id, token!),
      enabled: Boolean(token),
      staleTime: 5_000,
      refetchInterval: 10_000,
      refetchIntervalInBackground: false,
    })),
  });

  const deepEnabled = filters.deepOps && Boolean(token);
  const invoiceResults = useQueries({
    queries: deepEnabled
      ? filteredEvents.map((event) => ({
          queryKey: queryKeys.invoices(workspace, event.id),
          queryFn: () => invoicesApi.listByEvent(event.id, token!),
          staleTime: 60_000,
        }))
      : [],
  });
  const budgetResults = useQueries({
    queries: deepEnabled
      ? filteredEvents.map((event) => ({
          queryKey: queryKeys.budgets(workspace, event.id),
          queryFn: () => budgetsApi.list(event.id, token!),
          staleTime: 60_000,
        }))
      : [],
  });
  const taskResults = useQueries({
    queries: deepEnabled
      ? filteredEvents.map((event) => ({
          queryKey: queryKeys.tasks(workspace, event.id),
          queryFn: () => tasksApi.list(event.id, token!),
          staleTime: 60_000,
        }))
      : [],
  });

  const loadedCount = dashboardResults.filter((r) => r.isSuccess).length;
  const failedCount = dashboardResults.filter((r) => r.isError).length;
  const pending = dashboardResults.some((r) => r.isPending) && filteredEvents.length > 0;

  const rows = useMemo(() => {
    return filteredEvents
      .map((event, i) => {
        const data = dashboardResults[i]?.data;
        if (!data) return null;
        const revenue = actualRevenueOf(data);
        const cost = totalCostOf(data);
        const profit = actualProfitOf(data);
        const margin = revenue > 0 ? (profit / revenue) * 100 : null;
        const projected = Number(data.metrics.projected_revenue || 0);
        const fin = data.event.finances;
        return {
          event,
          revenue,
          cost,
          profit,
          margin,
          projected,
          attainment: projected > 0 ? (revenue / projected) * 100 : null,
          ticketsSold: Number(data.ticket_summary.tickets_sold || 0),
          totalTickets: Number(data.ticket_summary.total_tickets || 0),
          sellThrough: Number(data.ticket_summary.sell_through_pct || 0),
          breakEven: Number(data.metrics.break_even_tickets || 0),
          doorEntries: Number(data.metrics.door_entries || 0),
          health: data.metrics.health_score ?? null,
          riskLevel: (data.risk_assessment?.overall_level || "unknown").toString().toLowerCase(),
          production: Number(fin?.production_cost || 0),
          marketing: Number(fin?.marketing_cost || 0),
          artistFee: Number(fin?.artist_fee || 0),
          staffing: data.staffing ?? null,
          tiers: data.ticket_summary.tiers ?? [],
        };
      })
      .filter((r): r is NonNullable<typeof r> => r !== null);
  }, [filteredEvents, dashboardResults]);

  const sortedRows = useMemo(() => {
    const dir = filters.sortDir === "asc" ? 1 : -1;
    const keyed = [...rows];
    keyed.sort((a, b) => {
      switch (filters.sort) {
        case "revenue":
          return (a.revenue - b.revenue) * dir;
        case "profit":
          return (a.profit - b.profit) * dir;
        case "margin":
          return ((a.margin ?? -Infinity) - (b.margin ?? -Infinity)) * dir;
        case "sellThrough":
          return (a.sellThrough - b.sellThrough) * dir;
        default:
          return (eventTime(a.event) - eventTime(b.event)) * dir;
      }
    });
    return keyed;
  }, [rows, filters.sort, filters.sortDir]);

  const totals = useMemo(() => {
    const acc = rows.reduce(
      (a, r) => {
        a.revenue += r.revenue;
        a.cost += r.cost;
        a.profit += r.profit;
        a.projected += r.projected;
        a.ticketsSold += r.ticketsSold;
        a.totalTickets += r.totalTickets;
        a.doorEntries += r.doorEntries;
        a.production += r.production;
        a.marketing += r.marketing;
        a.artistFee += r.artistFee;
        a.breakEvenHits += r.breakEven > 0 && r.ticketsSold >= r.breakEven ? 1 : 0;
        a.healthSum += r.health ?? 0;
        a.healthCount += r.health !== null ? 1 : 0;
        if (r.staffing) {
          a.security += r.staffing.security_required;
          a.volunteers += r.staffing.volunteers_required;
          a.medics += r.staffing.medics_required;
          a.expectedAttendees += r.staffing.expected_attendees;
        }
        return a;
      },
      {
        revenue: 0, cost: 0, profit: 0, projected: 0, ticketsSold: 0, totalTickets: 0, doorEntries: 0,
        production: 0, marketing: 0, artistFee: 0, breakEvenHits: 0, healthSum: 0, healthCount: 0,
        security: 0, volunteers: 0, medics: 0, expectedAttendees: 0,
      },
    );
    const margin = acc.revenue > 0 ? (acc.profit / acc.revenue) * 100 : null;
    const sellThrough = acc.totalTickets > 0 ? (acc.ticketsSold / acc.totalTickets) * 100 : null;
    const avgTicketPrice = acc.ticketsSold > 0 ? acc.revenue / acc.ticketsSold : null;
    const attainment = acc.projected > 0 ? (acc.revenue / acc.projected) * 100 : null;
    const roi = acc.cost > 0 ? (acc.profit / acc.cost) * 100 : null;
    const otherCost = Math.max(0, acc.cost - acc.production - acc.marketing - acc.artistFee);
    const avgHealth = acc.healthCount > 0 ? acc.healthSum / acc.healthCount : null;
    const breakEvenRate = rows.length > 0 ? (acc.breakEvenHits / rows.length) * 100 : null;
    return { ...acc, margin, sellThrough, avgTicketPrice, attainment, roi, otherCost, avgHealth, breakEvenRate, count: rows.length };
  }, [rows]);

  const cumulativeSeries = useMemo(() => {
    let runRevenue = 0;
    let runProfit = 0;
    return [...rows]
      .sort((a, b) => eventTime(a.event) - eventTime(b.event))
      .map((r) => {
        runRevenue += r.revenue;
        runProfit += r.profit;
        return {
          name: r.event.name,
          revenue: r.revenue,
          cost: r.cost,
          profit: r.profit,
          cumulativeRevenue: runRevenue,
          cumulativeProfit: runProfit,
          attainment: r.attainment ?? 0,
        };
      });
  }, [rows]);

  const byArtist = useMemo(() => aggregateBy(rows, (r) => r.event.artist || "Unattributed"), [rows]);
  const byCity = useMemo(() => aggregateBy(rows, (r) => r.event.city || "Unknown"), [rows]);
  const byGenre = useMemo(
    () => aggregateBy(rows, (r) => genreByArtistId.get(r.event.artist_id || "") || "Unlabelled"),
    [rows, genreByArtistId],
  );

  const statusBreakdown = useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach((r) => map.set(r.event.status, (map.get(r.event.status) || 0) + 1));
    return Array.from(map, ([name, value]) => ({ name, value }));
  }, [rows]);

  const riskBreakdown = useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach((r) => map.set(r.riskLevel, (map.get(r.riskLevel) || 0) + 1));
    return Array.from(map, ([name, value]) => ({ name, value }));
  }, [rows]);

  const costComposition = useMemo(
    () =>
      [
        { name: "Production", value: totals.production },
        { name: "Marketing", value: totals.marketing },
        { name: "Artist fees", value: totals.artistFee },
        { name: "Logistics / other", value: totals.otherCost },
      ].filter((d) => d.value > 0),
    [totals],
  );

  const tierMix = useMemo(() => {
    const map = new Map<string, { name: string; revenue: number; sold: number }>();
    rows.forEach((r) =>
      r.tiers.forEach((t) => {
        const cur = map.get(t.name) ?? { name: t.name, revenue: 0, sold: 0 };
        cur.revenue += Number(t.revenue || 0);
        cur.sold += Number(t.sold || 0);
        map.set(t.name, cur);
      }),
    );
    return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 8);
  }, [rows]);

  const scatterData = useMemo(
    () =>
      rows
        .filter((r) => r.margin !== null)
        .map((r) => ({ x: r.sellThrough, y: r.margin as number, z: Math.max(1, r.revenue), name: r.event.name })),
    [rows],
  );

  const deepOps = useMemo(() => {
    if (!deepEnabled) return null;
    const invoices = invoiceResults.flatMap((r) => (r.data as InvoiceRecord[] | undefined) ?? []);
    const budgets = budgetResults.flatMap((r) => (r.data as Budget[] | undefined) ?? []);
    const tasks = taskResults.flatMap((r) => (r.data as Task[] | undefined) ?? []);
    const billed = invoices.reduce((s, inv) => s + Number(inv.total || 0), 0);
    const tax = invoices.reduce((s, inv) => s + Number(inv.tax || 0), 0);
    const estimated = budgets.reduce((s, b) => s + Number(b.estimated_cost || 0), 0);
    const actual = budgets.reduce((s, b) => s + Number(b.actual_cost || 0), 0);
    const doneStatuses = new Set(["done", "completed", "complete", "closed"]);
    const tasksDone = tasks.filter((t) => doneStatuses.has((t.status || "").toLowerCase())).length;
    const loading =
      invoiceResults.some((r) => r.isPending) || budgetResults.some((r) => r.isPending) || taskResults.some((r) => r.isPending);
    return {
      loading,
      invoiceCount: invoices.length,
      billed,
      tax,
      estimated,
      actual,
      variance: actual - estimated,
      tasksTotal: tasks.length,
      tasksDone,
      taskCompletion: tasks.length > 0 ? (tasksDone / tasks.length) * 100 : null,
    };
  }, [deepEnabled, invoiceResults, budgetResults, taskResults]);

  const insights = useMemo(() => {
    if (rows.length === 0) return [];
    const list: { label: string; value: string; tone: "good" | "bad" | "info" }[] = [];
    const topArtist = byArtist[0];
    if (topArtist) list.push({ label: "Top artist by revenue", value: `${topArtist.label} · ${formatINR(topArtist.revenue)}`, tone: "good" });
    const topCity = byCity[0];
    if (topCity) list.push({ label: "Top market", value: `${topCity.label} · ${formatINR(topCity.revenue)}`, tone: "info" });
    const bestMargin = [...rows].filter((r) => r.margin !== null).sort((a, b) => (b.margin ?? 0) - (a.margin ?? 0))[0];
    if (bestMargin) list.push({ label: "Highest margin event", value: `${bestMargin.event.name} · ${formatMarginPct(bestMargin.margin, bestMargin.revenue)}`, tone: "good" });
    const lossEvents = rows.filter((r) => r.profit < 0).length;
    list.push({ label: "Loss-making events", value: `${lossEvents} of ${rows.length}`, tone: lossEvents > 0 ? "bad" : "good" });
    if (totals.breakEvenRate !== null) list.push({ label: "Break-even hit rate", value: formatPct(totals.breakEvenRate), tone: totals.breakEvenRate >= 50 ? "good" : "bad" });
    if (totals.attainment !== null) list.push({ label: "Revenue attainment", value: formatPct(totals.attainment), tone: totals.attainment >= 80 ? "good" : "info" });
    return list;
  }, [rows, byArtist, byCity, totals]);

  const updateFilter = (patch: Partial<AnalyticsFilters>) => {
    const resetsWindow =
      !("offset" in patch) && ("mode" in patch || "lastN" in patch || "rollingValue" in patch || "rollingUnit" in patch);
    const next = { ...filters, ...patch, ...(resetsWindow ? { offset: 0 } : null) };
    const params = new URLSearchParams();
    if (next.mode !== DEFAULT_FILTERS.mode) params.set("mode", next.mode);
    if (next.mode === "lastN") {
      params.set("n", String(next.lastN));
      if (next.offset > 0) params.set("off", String(next.offset));
    }
    if (next.mode === "rolling") {
      params.set("unit", next.rollingUnit);
      params.set("v", String(next.rollingValue));
      if (next.offset > 0) params.set("off", String(next.offset));
    }
    if (next.mode === "range") {
      if (next.from) params.set("from", next.from);
      if (next.to) params.set("to", next.to);
    }
    if (next.artist !== "all") params.set("artist", next.artist);
    if (next.genre !== "all") params.set("genre", next.genre);
    if (next.venue !== "all") params.set("venue", next.venue);
    if (next.city !== "all") params.set("city", next.city);
    if (next.status !== "all") params.set("status", next.status);
    if (next.search.trim()) params.set("q", next.search.trim());
    if (next.sort !== DEFAULT_FILTERS.sort) params.set("sort", next.sort);
    if (next.sortDir !== DEFAULT_FILTERS.sortDir) params.set("dir", next.sortDir);
    if (next.deepOps) params.set("deep", "1");
    setSearchParams(params, { replace: true });
  };

  const resetFilters = () => setSearchParams(new URLSearchParams(), { replace: true });

  const exportCsv = () => {
    const header = ["Event", "City", "Date", "Status", "Revenue", "Spend", "Profit", "Margin%", "Sell-through%", "Tickets sold", "Break-even", "Health"];
    const lines = sortedRows.map((r) =>
      [
        `"${r.event.name.replace(/"/g, '""')}"`,
        `"${r.event.city ?? ""}"`,
        r.event.event_date,
        r.event.status,
        r.revenue.toFixed(2),
        r.cost.toFixed(2),
        r.profit.toFixed(2),
        r.margin === null ? "" : r.margin.toFixed(1),
        r.sellThrough.toFixed(1),
        r.ticketsSold,
        r.breakEven,
        r.health ?? "",
      ].join(","),
    );
    const totalLine = ["TOTAL", "", "", "", totals.revenue.toFixed(2), totals.cost.toFixed(2), totals.profit.toFixed(2), totals.margin?.toFixed(1) ?? "", totals.sellThrough?.toFixed(1) ?? "", totals.ticketsSold, "", ""].join(",");
    const csv = [header.join(","), ...lines, totalLine].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `portfolio_analytics_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const errorMsg =
    eventsQuery.error instanceof Error ? eventsQuery.error.message : eventsQuery.error ? String(eventsQuery.error) : "";

  const rollingWindowLabel = selection.rollingWindow
    ? `${formatWindowDate(selection.rollingWindow.start)} – ${
        filters.offset === 0 ? "today" : formatWindowDate(selection.rollingWindow.end)
      }`
    : "";

  const filterSummary = (() => {
    if (filters.mode === "lastN") {
      return filters.offset === 0
        ? `Last ${filters.lastN} events`
        : `Events ${filters.offset * filters.lastN + 1}–${(filters.offset + 1) * filters.lastN} back`;
    }
    if (filters.mode === "rolling") {
      return filters.offset === 0
        ? `Last ${filters.rollingValue} ${filters.rollingUnit}`
        : `${filters.rollingValue} ${filters.rollingUnit} · ${rollingWindowLabel}`;
    }
    if (filters.mode === "range") return `${filters.from || "start"} → ${filters.to || "now"}`;
    return "All events";
  })();

  const rangeSpanDays =
    filters.from && filters.to ? Math.round((Date.parse(filters.to) - Date.parse(filters.from)) / DAY_MS) + 1 : 0;
  const canShiftRange = Number.isFinite(rangeSpanDays) && rangeSpanDays >= 1;
  const showPeriodNav = filters.mode !== "all";
  const prevDisabled = !selection.hasOlder || (filters.mode === "range" && !canShiftRange);
  const nextDisabled =
    filters.mode === "range"
      ? !canShiftRange || shiftDateInput(filters.to, rangeSpanDays) > localDateInputValue(new Date())
      : filters.offset === 0;

  const stepPeriod = (direction: -1 | 1) => {
    if (filters.mode === "range") {
      const days = direction * rangeSpanDays;
      updateFilter({ from: shiftDateInput(filters.from, days), to: shiftDateInput(filters.to, days) });
      return;
    }
    updateFilter({ offset: Math.max(0, filters.offset - direction) });
  };

  return (
    <div className="app-shell-dash analytics-page">
      <div className="app-shell-dash-toolbar">
        <div>
          <div className="app-shell-dash-toolbar-meta">
            <span className="app-shell-pill app-shell-pill--accent">{filterSummary}</span>
            <span className="app-shell-pill">{totals.count} events in view</span>
            {failedCount > 0 ? <span className="app-shell-pill analytics-page__pill-warn">{failedCount} failed</span> : null}
          </div>
        </div>
        <div className="analytics-page__toolbar-actions">
          <button type="button" className="eventos-nav-btn" onClick={exportCsv} disabled={rows.length === 0}>
            Export CSV
          </button>
          <button type="button" className="eventos-nav-btn" onClick={() => void eventsQuery.refetch()} disabled={!token || eventsQuery.isFetching}>
            {eventsQuery.isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {eventsQuery.isError ? (
        <QueryErrorBanner message={errorMsg || "Request failed"} onRetry={() => void eventsQuery.refetch()} retrying={eventsQuery.isFetching} />
      ) : null}

      {/* ── Filter bar ── */}
      <div className="eventos-card analytics-page__filters">
        <div className="analytics-page__filters-row">
          <div className="analytics-page__filter-group analytics-page__filter-group--grow">
            <span className="analytics-page__filter-label">Search</span>
            <input
              type="search"
              className="eventos-input"
              placeholder="Event, artist, or city…"
              value={filters.search}
              onChange={(e) => updateFilter({ search: e.target.value })}
              aria-label="Search events"
            />
          </div>

          <div className="analytics-page__filter-group" role="group" aria-label="Time window">
            <span className="analytics-page__filter-label">Window</span>
            <div className="analytics-page__segmented">
              {(["all", "lastN", "rolling", "range"] as RangeMode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`analytics-page__seg-btn${filters.mode === m ? " is-active" : ""}`}
                  onClick={() => updateFilter({ mode: m })}
                >
                  {m === "all" ? "All" : m === "lastN" ? "Last N" : m === "rolling" ? "Rolling" : "Range"}
                </button>
              ))}
            </div>
          </div>

          {filters.mode === "lastN" ? (
            <div className="analytics-page__filter-group">
              <span className="analytics-page__filter-label">Count</span>
              <input
                type="number"
                min={1}
                className="eventos-input analytics-page__num"
                value={filters.lastN}
                onChange={(e) => updateFilter({ lastN: Math.max(1, Number(e.target.value) || 1) })}
                aria-label="Number of recent events"
              />
            </div>
          ) : null}

          {filters.mode === "rolling" ? (
            <div className="analytics-page__filter-group">
              <span className="analytics-page__filter-label">Period</span>
              <div className="analytics-page__inline">
                <input
                  type="number"
                  min={1}
                  className="eventos-input analytics-page__num"
                  value={filters.rollingValue}
                  onChange={(e) => updateFilter({ rollingValue: Math.max(1, Number(e.target.value) || 1) })}
                  aria-label="Rolling period value"
                />
                <MobileSelect
                  label="Rolling period unit"
                  className="eventos-select"
                  value={filters.rollingUnit}
                  onChange={(v) => updateFilter({ rollingUnit: v as RollingUnit })}
                  options={[
                    { value: "days", label: "Days" },
                    { value: "weeks", label: "Weeks" },
                    { value: "months", label: "Months" },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {filters.mode === "range" ? (
            <div className="analytics-page__filter-group">
              <span className="analytics-page__filter-label">Date range</span>
              <div className="analytics-page__inline">
                <input type="date" className="eventos-input" value={filters.from} onChange={(e) => updateFilter({ from: e.target.value })} aria-label="From date" />
                <input type="date" className="eventos-input" value={filters.to} onChange={(e) => updateFilter({ to: e.target.value })} aria-label="To date" />
              </div>
            </div>
          ) : null}

          {showPeriodNav ? (
            <div className="analytics-page__filter-group" role="group" aria-label="Move time window">
              <span className="analytics-page__filter-label">Navigate</span>
              <div className="analytics-page__period-nav">
                <button
                  type="button"
                  className="eventos-nav-btn analytics-page__period-btn"
                  onClick={() => stepPeriod(-1)}
                  disabled={prevDisabled}
                  aria-label="Previous period"
                  title="Previous period"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="eventos-nav-btn analytics-page__period-btn"
                  onClick={() => stepPeriod(1)}
                  disabled={nextDisabled}
                  aria-label="Next period"
                  title="Next period"
                >
                  ›
                </button>
                {filters.mode === "rolling" ? (
                  <span className="analytics-page__period-label" aria-live="polite">{rollingWindowLabel}</span>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        <div className="analytics-page__filters-row analytics-page__filters-row--secondary">
          <div className="analytics-page__filter-group">
            <span className="analytics-page__filter-label">Artist</span>
            <MobileSelect
              label="Filter by artist"
              className="eventos-select"
              value={filters.artist}
              onChange={(v) => updateFilter({ artist: v })}
              options={[
                { value: "all", label: "All artists" },
                ...artistOptions.map((opt) => ({ value: opt.value, label: opt.label })),
              ]}
            />
          </div>

          <div className="analytics-page__filter-group">
            <span className="analytics-page__filter-label">Genre</span>
            <MobileSelect
              label="Filter by genre"
              className="eventos-select"
              value={filters.genre}
              onChange={(v) => updateFilter({ genre: v })}
              options={[
                { value: "all", label: "All genres" },
                ...genreOptions.map((g) => ({ value: g, label: g })),
              ]}
            />
          </div>

          <div className="analytics-page__filter-group">
            <span className="analytics-page__filter-label">Venue</span>
            <MobileSelect
              label="Filter by venue"
              className="eventos-select"
              value={filters.venue}
              onChange={(v) => updateFilter({ venue: v })}
              options={[
                { value: "all", label: "All venues" },
                ...venueOptions.map((opt) => ({ value: opt.value, label: opt.label })),
              ]}
            />
          </div>

          <div className="analytics-page__filter-group">
            <span className="analytics-page__filter-label">Location</span>
            <MobileSelect
              label="Filter by city"
              className="eventos-select"
              value={filters.city}
              onChange={(v) => updateFilter({ city: v })}
              options={[
                { value: "all", label: "All cities" },
                ...cityOptions.map((city) => ({ value: city, label: city })),
              ]}
            />
          </div>

          <div className="analytics-page__filter-group">
            <span className="analytics-page__filter-label">Status</span>
            <MobileSelect
              label="Filter by status"
              className="eventos-select"
              value={filters.status}
              onChange={(v) => updateFilter({ status: v })}
              options={[
                { value: "all", label: "All statuses" },
                ...statusOptions.map((status) => ({ value: status, label: status.replace(/_/g, " ") })),
              ]}
            />
          </div>

          <label className="analytics-page__toggle">
            <input type="checkbox" checked={filters.deepOps} onChange={(e) => updateFilter({ deepOps: e.target.checked })} />
            <span>Billing &amp; ops</span>
          </label>

          <button type="button" className="eventos-nav-btn analytics-page__reset" onClick={resetFilters}>
            Reset
          </button>
        </div>
      </div>

      {pending ? (
        <div className="analytics-page__loading-note eventos-subtle">
          Aggregating {loadedCount}/{filteredEvents.length} event dashboards…
        </div>
      ) : null}

      {/* ── Headline KPIs ── */}
      {eventsQuery.isPending ? (
        <KpiStripSkeleton count={6} />
      ) : (
        <div className="app-shell-kpi-strip analytics-page__kpis">
          <StatCard label="Events" value={totals.count.toLocaleString("en-IN")} sub="In current view" />
          <StatCard label="Revenue" value={formatINR(totals.revenue)} sub="Σ actual" tone="profit" />
          <StatCard label="Profit" value={formatINR(totals.profit)} sub="Σ net" tone={totals.profit >= 0 ? "profit" : "loss"} />
          <StatCard label="Spend" value={formatINR(totals.cost)} sub="Σ total cost" tone="loss" />
          <StatCard label="Avg margin" value={formatMarginPct(totals.margin, totals.revenue)} sub="Profit / revenue" tone="neutral" />
          <StatCard label="ROI" value={formatPct(totals.roi)} sub="Profit / spend" tone="neutral" />
          <StatCard label="Tickets sold" value={totals.ticketsSold.toLocaleString("en-IN")} sub="Across events" />
          <StatCard label="Sell-through" value={formatSellThroughPct(totals.sellThrough, totals.ticketsSold)} sub="Sold / capacity" tone="neutral" />
          <StatCard label="Avg ticket" value={totals.avgTicketPrice === null ? "—" : formatINR(totals.avgTicketPrice)} sub="Revenue / ticket" />
          <StatCard label="Attainment" value={formatPct(totals.attainment)} sub="Actual / projected" tone="neutral" />
          <StatCard label="Break-even rate" value={formatPct(totals.breakEvenRate)} sub="Events past BE" tone="neutral" />
          <StatCard label="Avg health" value={totals.avgHealth === null ? "—" : Math.round(totals.avgHealth).toString()} sub="Event health score" />
        </div>
      )}

      {/* ── Deep ops KPIs ── */}
      {deepOps ? (
        <div className="app-shell-kpi-strip analytics-page__kpis">
          <StatCard label="Invoices" value={deepOps.invoiceCount.toLocaleString("en-IN")} sub={deepOps.loading ? "Loading…" : "Generated"} />
          <StatCard label="Billed" value={formatINR(deepOps.billed)} sub="Σ invoice total" tone="profit" />
          <StatCard label="Tax collected" value={formatINR(deepOps.tax)} sub="Σ GST / tax" tone="neutral" />
          <StatCard label="Budget est." value={formatINR(deepOps.estimated)} sub="Σ estimated" />
          <StatCard label="Budget actual" value={formatINR(deepOps.actual)} sub="Σ actual" tone="loss" />
          <StatCard label="Variance" value={formatINR(deepOps.variance)} sub="Actual − est." tone={deepOps.variance > 0 ? "loss" : "profit"} />
          <StatCard label="Tasks" value={deepOps.tasksTotal.toLocaleString("en-IN")} sub="Across events" />
          <StatCard label="Task completion" value={formatPct(deepOps.taskCompletion)} sub={`${deepOps.tasksDone} done`} tone="neutral" />
        </div>
      ) : null}

      {!eventsQuery.isPending && filteredEvents.length === 0 ? (
        <div className="eventos-card analytics-page__empty">
          <div className="eventos-label">No events match these filters</div>
          <p className="eventos-subtle">Try widening the time window or clearing artist/genre/venue/location/status filters.</p>
          <button type="button" className="eventos-nav-btn analytics-page__reset" onClick={resetFilters}>
            Reset filters
          </button>
        </div>
      ) : (
        <>
          {/* ── Auto insights ── */}
          {insights.length > 0 ? (
            <div className="analytics-page__insights">
              {insights.map((ins) => (
                <div key={ins.label} className={`analytics-page__insight analytics-page__insight--${ins.tone}`}>
                  <span className="analytics-page__insight-label">{ins.label}</span>
                  <span className="analytics-page__insight-value">{ins.value}</span>
                </div>
              ))}
            </div>
          ) : null}

          {/* ── Cumulative trend ── */}
          <div className="eventos-card analytics-page__chart-card">
            <div className="eventos-label">Cumulative revenue &amp; profit over time</div>
            {cumulativeSeries.length === 0 ? (
              <div className="eventos-empty">Data will appear once dashboards finish loading.</div>
            ) : (
              <div className="analytics-page__chart">
                <ResponsiveContainer>
                  <AreaChart data={cumulativeSeries} margin={{ top: 16, right: 12, left: 0, bottom: 14 }}>
                    <defs>
                      <linearGradient id="cumRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={COLORS.revenue} stopOpacity={0.4} />
                        <stop offset="100%" stopColor={COLORS.revenue} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="cumProfit" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={COLORS.profit} stopOpacity={0.4} />
                        <stop offset="100%" stopColor={COLORS.profit} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="name" tick={CHART_AXIS_TICK} interval="preserveStartEnd" tickFormatter={trimLabel} />
                    <YAxis tickFormatter={formatINRCompact} tick={CHART_AXIS_TICK} width={70} />
                    <Tooltip formatter={(value: number | string, name: string) => [formatINR(Number(value) || 0), name]} {...chartTooltipProps} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area type="monotone" dataKey="cumulativeRevenue" name="Cumulative revenue" stroke={COLORS.revenue} fill="url(#cumRev)" strokeWidth={2.5} />
                    <Area type="monotone" dataKey="cumulativeProfit" name="Cumulative profit" stroke={COLORS.profit} fill="url(#cumProfit)" strokeWidth={2.5} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* ── Per-event revenue/spend/profit + attainment ── */}
          <div className="eventos-card analytics-page__chart-card">
            <div className="eventos-label">Revenue · spend · profit · attainment by event</div>
            {cumulativeSeries.length === 0 ? (
              <div className="eventos-empty">Loading…</div>
            ) : (
              <div className="analytics-page__chart">
                <ResponsiveContainer>
                  <ComposedChart data={cumulativeSeries} margin={{ top: 16, right: 12, left: 0, bottom: 14 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="name" tick={CHART_AXIS_TICK} interval="preserveStartEnd" tickFormatter={trimLabel} />
                    <YAxis yAxisId="left" tickFormatter={formatINRCompact} tick={CHART_AXIS_TICK} width={70} />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={(v: number) => `${Math.round(v)}%`} tick={CHART_AXIS_TICK} width={48} />
                    <Tooltip
                      formatter={(value: number | string, name: string) =>
                        name === "Attainment" ? [`${Number(value).toFixed(1)}%`, name] : [formatINR(Number(value) || 0), name]
                      }
                      {...chartTooltipProps}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill={COLORS.revenue} radius={[6, 6, 0, 0]} />
                    <Bar yAxisId="left" dataKey="cost" name="Spend" fill={COLORS.spend} radius={[6, 6, 0, 0]} />
                    <Line yAxisId="left" type="monotone" dataKey="profit" name="Profit" stroke={COLORS.profit} strokeWidth={2.5} dot={{ r: 3 }} />
                    <Line yAxisId="right" type="monotone" dataKey="attainment" name="Attainment" stroke={COLORS.amber} strokeWidth={2} strokeDasharray="5 4" dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* ── Breakdowns row ── */}
          <div className="analytics-page__split">
            <ChartCard title="Revenue by artist">
              {byArtist.length === 0 ? <Empty /> : <HBar data={byArtist} color="#A855F7" />}
            </ChartCard>
            <ChartCard title="Revenue by location">
              {byCity.length === 0 ? <Empty /> : <HBar data={byCity} color="#06B6D4" />}
            </ChartCard>
          </div>

          <div className="analytics-page__split">
            <ChartCard title="Revenue by genre">
              {byGenre.length === 0 ? <Empty /> : <HBar data={byGenre} color="#22C55E" />}
            </ChartCard>
            <ChartCard title="Cost composition">
              {costComposition.length === 0 ? (
                <Empty />
              ) : (
                <div className="analytics-page__chart analytics-page__chart--sm">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={costComposition} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={84} paddingAngle={2}>
                        {costComposition.map((entry, i) => (
                          <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number | string, name: string) => [formatINR(Number(value) || 0), name]} {...chartTooltipProps} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </ChartCard>
          </div>

          <div className="analytics-page__split">
            <ChartCard title="Events by status">
              {statusBreakdown.length === 0 ? (
                <Empty />
              ) : (
                <div className="analytics-page__chart analytics-page__chart--sm">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={statusBreakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={84} paddingAngle={2}>
                        {statusBreakdown.map((entry, i) => (
                          <Cell key={entry.name} fill={STATUS_COLORS[entry.name.toLowerCase()] ?? PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number | string, name: string) => [`${value} events`, String(name).replace(/_/g, " ")]} {...chartTooltipProps} />
                      <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v: string) => v.replace(/_/g, " ")} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </ChartCard>
            <ChartCard title="Risk distribution">
              {riskBreakdown.length === 0 ? (
                <Empty />
              ) : (
                <div className="analytics-page__chart analytics-page__chart--sm">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={riskBreakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={84} paddingAngle={2}>
                        {riskBreakdown.map((entry, i) => (
                          <Cell key={entry.name} fill={RISK_COLORS[entry.name] ?? PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number | string, name: string) => [`${value} events`, name]} {...chartTooltipProps} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </ChartCard>
          </div>

          <div className="analytics-page__split">
            <ChartCard title="Ticket tier mix (revenue)">
              {tierMix.length === 0 ? <Empty /> : <HBar data={tierMix.map((t) => ({ label: t.name, revenue: t.revenue }))} color="#F59E0B" />}
            </ChartCard>
            <ChartCard title="Sell-through vs margin">
              {scatterData.length === 0 ? (
                <Empty />
              ) : (
                <div className="analytics-page__chart analytics-page__chart--sm">
                  <ResponsiveContainer>
                    <ScatterChart margin={{ top: 16, right: 16, left: 0, bottom: 14 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                      <XAxis type="number" dataKey="x" name="Sell-through" unit="%" tick={CHART_AXIS_TICK} domain={[0, 100]} />
                      <YAxis type="number" dataKey="y" name="Margin" unit="%" tick={CHART_AXIS_TICK} width={48} />
                      <ZAxis type="number" dataKey="z" range={[60, 400]} name="Revenue" />
                      <Tooltip
                        cursor={{ strokeDasharray: "3 3" }}
                        {...chartTooltipProps}
                        formatter={(value: number | string, name: string) => {
                          if (name === "Revenue") return [formatINR(Number(value) || 0), name];
                          return [`${Number(value).toFixed(1)}%`, name];
                        }}
                      />
                      <Scatter data={scatterData} fill={COLORS.profit}>
                        {scatterData.map((entry, i) => (
                          <Cell key={`${entry.name}-${i}`} fill={entry.y >= 0 ? COLORS.revenue : COLORS.spend} />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              )}
            </ChartCard>
          </div>

          {/* ── Operational summary ── */}
          <div className="eventos-card analytics-page__ops">
            <div className="eventos-label">Operational footprint</div>
            <div className="analytics-page__ops-grid">
              <OpsStat label="Expected attendees" value={totals.expectedAttendees.toLocaleString("en-IN")} />
              <OpsStat label="Door entries" value={totals.doorEntries.toLocaleString("en-IN")} />
              <OpsStat label="Security required" value={totals.security.toLocaleString("en-IN")} />
              <OpsStat label="Volunteers required" value={totals.volunteers.toLocaleString("en-IN")} />
              <OpsStat label="Medics required" value={totals.medics.toLocaleString("en-IN")} />
              <OpsStat label="Total capacity" value={totals.totalTickets.toLocaleString("en-IN")} />
            </div>
          </div>

          {/* ── Leaderboard table ── */}
          <div className="app-shell-dash-panel">
            <div className="app-shell-dash-panel-head">
              <h2 className="app-shell-dash-panel-title">Per-event contribution</h2>
              <div className="analytics-page__sort">
                <label className="eventos-subtle" htmlFor="analytics-sort">Sort</label>
                <MobileSelect
                  id="analytics-sort"
                  label="Sort by"
                  className="eventos-select"
                  value={filters.sort}
                  onChange={(v) => updateFilter({ sort: v as SortKey })}
                  options={[
                    { value: "date", label: "Date" },
                    { value: "revenue", label: "Revenue" },
                    { value: "profit", label: "Profit" },
                    { value: "margin", label: "Margin" },
                    { value: "sellThrough", label: "Sell-through" },
                  ]}
                />
                <button
                  type="button"
                  className="eventos-nav-btn analytics-page__row-link"
                  onClick={() => updateFilter({ sortDir: filters.sortDir === "asc" ? "desc" : "asc" })}
                  aria-label="Toggle sort direction"
                >
                  {filters.sortDir === "asc" ? "↑ Asc" : "↓ Desc"}
                </button>
              </div>
            </div>
            <div className="app-shell-dash-panel-body">
              <div className="app-shell-table-wrap">
                <table className="app-shell-table analytics-page__table">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>City</th>
                      <th>Date</th>
                      <th className="analytics-page__num-head">Revenue</th>
                      <th className="analytics-page__num-head">Spend</th>
                      <th className="analytics-page__num-head">Profit</th>
                      <th className="analytics-page__num-head">Margin</th>
                      <th className="analytics-page__num-head">Sell-thru</th>
                      <th className="analytics-page__num-head">Health</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="analytics-page__empty-cell">
                          {pending ? "Loading event financials…" : "No financial data available."}
                        </td>
                      </tr>
                    ) : (
                      sortedRows.map((r) => (
                        <tr key={r.event.id}>
                          <td>
                            <strong>{r.event.name}</strong>
                            {r.event.artist ? <div className="eventos-subtle">{r.event.artist}</div> : null}
                          </td>
                          <td>{r.event.city}</td>
                          <td>{new Date(r.event.event_date).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })}</td>
                          <td className="analytics-page__num-cell">{formatINR(r.revenue)}</td>
                          <td className="analytics-page__num-cell">{formatINR(r.cost)}</td>
                          <td className={`analytics-page__num-cell ${r.profit >= 0 ? "is-up" : "is-down"}`}>{formatINR(r.profit)}</td>
                          <td className="analytics-page__num-cell">{formatMarginPct(r.margin, r.revenue)}</td>
                          <td className="analytics-page__num-cell">{r.sellThrough.toFixed(0)}%</td>
                          <td className="analytics-page__num-cell">{r.health ?? "—"}</td>
                          <td>
                            <Link to={workspaceNav.dashboard(workspace, r.event.id)} className="eventos-nav-btn analytics-page__row-link">
                              Open
                            </Link>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {sortedRows.length > 0 ? (
                    <tfoot>
                      <tr className="analytics-page__total-row">
                        <td colSpan={3}>Totals</td>
                        <td className="analytics-page__num-cell">{formatINR(totals.revenue)}</td>
                        <td className="analytics-page__num-cell">{formatINR(totals.cost)}</td>
                        <td className={`analytics-page__num-cell ${totals.profit >= 0 ? "is-up" : "is-down"}`}>{formatINR(totals.profit)}</td>
                        <td className="analytics-page__num-cell">{formatMarginPct(totals.margin, totals.revenue)}</td>
                        <td className="analytics-page__num-cell">{totals.sellThrough === null ? "—" : `${totals.sellThrough.toFixed(0)}%`}</td>
                        <td className="analytics-page__num-cell">{totals.avgHealth === null ? "—" : Math.round(totals.avgHealth)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* ── Helpers & small presentational components ── */

function aggregateBy(
  rows: Array<{ revenue: number; profit: number; event: EventListItem } & Record<string, unknown>>,
  keyFn: (r: { revenue: number; profit: number; event: EventListItem } & Record<string, unknown>) => string,
): Array<{ label: string; revenue: number; profit: number }> {
  const map = new Map<string, { label: string; revenue: number; profit: number }>();
  rows.forEach((r) => {
    const label = keyFn(r);
    const cur = map.get(label) ?? { label, revenue: 0, profit: 0 };
    cur.revenue += r.revenue;
    cur.profit += r.profit;
    map.set(label, cur);
  });
  return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 8);
}

function trimLabel(label: string): string {
  return label.length > 10 ? `${label.slice(0, 9)}…` : label;
}

const StatCard: React.FC<{ label: string; value: string; sub?: string; tone?: "profit" | "loss" | "neutral" }> = ({
  label,
  value,
  sub,
  tone,
}) => (
  <div className="app-shell-kpi">
    <span className="app-shell-kpi-label">{label}</span>
    <span className={`app-shell-kpi-value${tone ? ` app-shell-kpi-value--${tone}` : ""}`}>{value}</span>
    {sub ? <span className="app-shell-kpi-sub">{sub}</span> : null}
  </div>
);

const OpsStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="analytics-page__ops-stat">
    <span className="analytics-page__ops-label">{label}</span>
    <span className="analytics-page__ops-value">{value}</span>
  </div>
);

const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="eventos-card analytics-page__chart-card">
    <div className="eventos-label">{title}</div>
    {children}
  </div>
);

const Empty: React.FC = () => <div className="eventos-empty">No data in current view.</div>;

const HBar: React.FC<{ data: Array<{ label: string; revenue: number }>; color: string }> = ({ data, color }) => (
  <div className="analytics-page__chart analytics-page__chart--sm">
    <ResponsiveContainer>
      <BarChart data={data} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" horizontal={false} />
        <XAxis type="number" tickFormatter={formatINRCompact} tick={CHART_AXIS_TICK} />
        <YAxis
          type="category"
          dataKey="label"
          tick={CHART_AXIS_TICK}
          width={110}
          tickFormatter={(l: string) => (l.length > 14 ? `${l.slice(0, 13)}…` : l)}
        />
        <Tooltip formatter={(value: number | string) => [formatINR(Number(value) || 0), "Revenue"]} {...chartTooltipProps} cursor={CHART_BAR_CURSOR} />
        <Bar dataKey="revenue" name="Revenue" radius={[0, 8, 8, 0]}>
          {data.map((entry) => (
            <Cell key={entry.label} fill={color} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

export default EventsAnalyticsPage;
